/**
 * Account > How to reach you, and Notifications (texting, 2026-10-31; spec
 * `Claude outputs/texting-2026-10-31/SPEC.md` "API contract" and "Frontend").
 *
 * Channels are per person (the email, the phone, each confirmed or not);
 * notifications are per event, with an Email tick and a Text tick. One event
 * for now: `arrival`, "When someone leaves me something".
 *
 * readReach() takes GET/POST `reader/account/notifications/` and gives the
 * page one shape. An older server answered only the email switch
 * ({ on, locked, reason, since, source }); that reads as the arrival event
 * with Email set and no phone, so the page works against either.
 */
import { formatPhone } from '@/utils/gift-send'

export const ARRIVAL_LABEL = 'When someone leaves me something'
export const ARRIVAL_SUB = 'A gift, or a message waiting. Never more than one email a day.'
export const TEXT_NEEDS_PHONE = 'Add a phone above'
export const TEXT_DISCLOSURE = 'Msg & data rates may apply. Reply STOP to stop, HELP for help.'

const pick = (a, b) => (a !== undefined && a !== null ? a : b)

export function readReach (payload) {
  const d = payload || {}
  const raw = Array.isArray(d.events) && d.events.length
    ? d.events
    : [{ event: 'arrival', label: ARRIVAL_LABEL, email: !!d.on, text: false }]
  const events = raw.filter((e) => e && e.event).map((e) => ({
    event: e.event,
    label: e.label || (e.event === 'arrival' ? ARRIVAL_LABEL : e.event),
    email: !!e.email,
    text: !!e.text,
    // The email's place on the opt-out list (a bounce locks it).
    locked: !!pick(e.locked, d.locked),
    reason: pick(e.reason, d.reason) || '',
    since: pick(e.since, d.since) || null,
    source: pick(e.source, d.source) || '',
  }))
  const phone = d.phone || ''
  return {
    email: d.email || '',
    // null: the server did not say (an older one); the page then says nothing.
    emailVerified: typeof d.email_verified === 'boolean' ? d.email_verified : null,
    phone,
    phoneVerified: !!(phone && d.phone_verified),
    smsEnabled: !!d.sms_enabled,
    events,
  }
}

/** "+1 (808) 555-0134" for a US number in E.164; any other country as stored. */
export function shownPhone (e164) {
  return e164 ? formatPhone(e164) : ''
}

/** The reason a phone or notification call did not go: a short code. */
export function reachReason (error) {
  const r = error && error.response
  const data = (r && r.data) || (error && error.ok === false ? error : {}) || {}
  if (data.reason) return data.reason
  if (typeof data.error === 'string' && /^[a-z_]+$/.test(data.error)) return data.error
  if (r && r.status === 409) return 'phone_taken'
  if (r && r.status === 429) return 'rate_limited'
  return ''
}

// Every reason the server answers (backend as built, 2026-10-31), as a plain
// sentence: the server's own words carry no full stop.
const LINES = {
  phone_taken: 'That number is already confirmed on another account.',
  sms_unavailable: "Texting isn't available yet. Check back soon.",
  phone_needed: 'Add a phone above first.',
  rate_limited: 'Too many codes for now. Try again in an hour.',
  invalid_phone: "That doesn't look like a phone number. Check it and try again.",
  opted_out: 'This number replied STOP to us. Text START in that same chat, then try again.',
  delivery_failed: "We couldn't text that number. Check it and try again.",
  bad_code: "That code didn't match. Check it, or send a new one.",
  expired: 'That code expired. Tap Send it again for a new one.',
  email_needed: 'Add your email above first.',
}
// Confirming a different number unticks Text (the yes was for the old one).
export const TEXT_UNTICKED_BY_CHANGE = 'New number confirmed. Tick Text again to get texts there.'

/** What to say for a call that did not go: ours for a known reason, else the server's words. */
export function reachMessage (error, fallback = 'That did not go through. Try again.') {
  const reason = reachReason(error)
  if (LINES[reason]) return LINES[reason]
  const said = error && error.response && error.response.data && error.response.data.error
  return (typeof said === 'string' && said.includes(' ')) ? said : fallback
}
